import React, { useCallback, useEffect, useMemo } from 'react';
import { TicketApi } from '../../../../redux/api/TicketApi';
import { ITicketProps } from '../../../../types/Ticket/ITicket';
import { PartialSpinny } from '../../../Nav/Spinny';
import { CardBody, CardHeader } from '@progress/kendo-react-layout';
import * as svg from '@progress/kendo-svg-icons';
import { IAction, IActionDTO } from '../../../../types/Action/IAction';
import { Api } from '../../../../redux/api/BaseApi';
import { useReduxDispatch, useReduxSelector } from '../../../../redux/hooks';
import FilterBanner from './Tools/Filter/FilterBanner';
import { SvgIcon } from '@progress/kendo-react-common';
import { Skeleton } from '@progress/kendo-react-indicators';
import { UserApi } from '../../../../redux/api/UserApi';
import { Setting } from '../../../../types/Setting/Setting';
import { PageChangeEvent } from '@progress/kendo-react-data-tools';
import ActionPager from './Pager';
import { useGetHostConfigQuery } from '../../../../redux/api/AccountApi';
import { Card } from 'react-bootstrap';
import { WorkflowApi } from '../../../../redux/api/WorkflowApi';
import { useNavigate } from 'react-router-dom';
import Action from './Action';
import { SetCurrentPageActions, SetSelectedActions } from '../../../../slices/SelectedActionsSlice';
import { ISetting } from '../../../../types/User/ISetting';
import { useCurrentUserContext } from '../../../../context/UserContext';
import { profile } from 'console';

const Actions: React.FC<ITicketProps> = (ticket) => {
    const navigate = useNavigate();
    const [page, setPage] = React.useState<PageInterface>({ skip: 0, take: null });
    //Filter the actions
    const filter = useReduxSelector(state => state.filter);
    const { profile: userProfile } = useCurrentUserContext();
    const { data: userPageSizeSetting } = UserApi.useGetSettingQuery(Setting.TICKET_DETAILS_PAGE_SIZE)
    const [savePageSetting] = UserApi.useSaveSettingMutation()
    const { data: actions, refetch, isFetching } = TicketApi.useGetActionsQuery({ ticketId: Number(ticket?.ID), take: page.take, skip: page.skip, ...filter }, { skip: ticket?.ID === undefined || page.take === null, pollingInterval: 30000 });
    const [updateAction, { isLoading: unpinning }] = TicketApi.useUpdateActionPinMutation();
    //Page size is and System setting
    const { data: hostConfig } = useGetHostConfigQuery(userProfile?.Tenant_Uid, { skip: !userProfile?.Tenant_Uid });
    const pageSize = userPageSizeSetting?.Int_Value ? Number(userPageSizeSetting.Int_Value) : hostConfig?.Call_Action_Page_Size || 20;
    const [completeWorkflowAction, { isLoading: completing }] = WorkflowApi.useCompleteActionMutation();
    const { selectedActions } = useReduxSelector(state => state.selectedActionsStore);
    const dispatch = useReduxDispatch();

    const actionChecked = (action: IAction) => {
        //Add or remove thew action from the selected actions
        const selected = selectedActions?.filter(a => a.actionId === action.ID).length > 0;
        console.log('actionChecked selected', selected, selectedActions?.length);
        if (selected) {
            let newSelectedActions = selectedActions?.filter(a => a.actionId !== action.ID);
            dispatch(SetSelectedActions(newSelectedActions?.length > 0 ? newSelectedActions : null));
        }
        else {
            dispatch(SetSelectedActions([...selectedActions || [], { actionId: action.ID, ticketId: action.Call_Id }]));
        }
    }

    //Cancel select action mode when the ticket changes
    useEffect(() => {
        dispatch(SetSelectedActions(null));
    }, [ticket?.ID]);

    //Load the current page actions ids into state for the select all / deselect all
    useEffect(() => {
        if (actions?.Items) {
            const selected = actions.Items.filter(a => !a.Is_System_Action).map(a => a.ID);
            dispatch(SetCurrentPageActions(selected));
        }
    }, [actions?.Items]);

    //group actions by Step_Name
    //const steps = [...new Set(actions?.Items?.slice().sort((a, b) => b?.Step_Order - a?.Step_Order).map(a => { return { Step_Id: a.Step_Id, Step_Name: a.Step_Name } }))];
    const hasWorkflow = actions?.Items?.some(a => a.Step_Id > 0);
    const steps = actions?.Items?.slice().sort((a, b) => b?.Step_Order - a?.Step_Order).map(a => { return { Step_Id: a.Step_Id, Step_Name: a.Step_Name, Workflow_Processing: a.Workflow_Processing } }).filter((value, index, self) =>
        index === self.findIndex((t) => (
            t.Step_Id === value.Step_Id && t.Step_Name === value.Step_Name
        ))
    );

    useEffect(() => {
        if (hostConfig || userPageSizeSetting)
            setPage(page => {
                return { ...page, take: userPageSizeSetting?.Int_Value || hostConfig?.Call_Action_Page_Size }
            });
    }, [hostConfig, userPageSizeSetting]);

    // Moved to server filtering, otherwise action paging doesn't work.
    //Sorting happens server side
    let filteredActions: IAction[] = actions?.Items || []; //.slice().sort((a, b) => new Date(b.Action_Dt)?.getTime() - new Date(a.Action_Dt)?.getTime()).sort((b, a) => a.Pinned - b.Pinned);
    //Filter out system actions and private actions if this is a mimic session and we are viewing the ticket as an end user. Normally the API won't return these actions, but in a mimic session we need to remove them
    if (userProfile?.Features.External_Interface) {
        filteredActions = filteredActions.filter(a => !a.Is_System_Action && a.Is_Private_Action !== true);
    }

    const handlePageChange = (event: PageChangeEvent) => {
        if (event.skip !== page.skip) {
            //If thew page number changes then scroll to the top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setPage({ skip: event.skip, take: event.take });


        //Save user page size setting IF it has changed
        if (event.take !== page.take)
            savePageSetting({ Setting_Id: Setting.TICKET_DETAILS_PAGE_SIZE, Int_Value: event.take } as ISetting);
    };

    const workflowStepCompleted = (action: IAction, edit: boolean) => {
        completeWorkflowAction({ id: action.Workflow_Action_Id }).unwrap().then(newActionId => {
            if (edit)
                navigate(`/ticket/${ticket.ID}/action/${newActionId}`);
        });
    }

    // const createActionJSX = useCallback((action: IAction) => {

    // }, [selectedActions]);

    const notesToRender = steps?.map(step => {
        const stepActions = filteredActions.filter(a => a.Step_Id === step.Step_Id);
        const actionNotes = stepActions.map((action) =>
            <Action key={action.ID} action={action} userProfile={userProfile} workflowStepCompleted={workflowStepCompleted} actionSelected={actionChecked} showSelect={selectedActions !== null} isSelected={selectedActions?.filter(s => s.actionId === action.ID).length > 0} />
        );

        return (
            hasWorkflow ? <Card>
                <CardHeader className='workflow-header'>
                    <h5>{step.Workflow_Processing !== null && <><img className='processing-image' src={step.Workflow_Processing === 0 ? './images/sequential-workflow.png' : './images/parallel-workflow.png'} title={step.Workflow_Processing === 0 ? 'sequential processing' : 'parallel processing'} />Workflow: </>}{step.Step_Name}</h5></CardHeader>
                <CardBody><div className='actions'>{actionNotes}</div></CardBody>
            </Card>
                : <div className='actions' >{actionNotes}</div>
        )
    });

    if (!actions?.Items || ticket.Is_Loading) return <>
        <Skeleton shape="rectangle" style={{ width: '100%', height: '60px', marginBottom: '10px' }} />
        <Skeleton shape="rectangle" style={{ width: '100%', height: '60px', marginBottom: '10px' }} />
        <Skeleton shape="rectangle" style={{ width: '100%', height: '60px', marginBottom: '10px' }} />
        <Skeleton shape="rectangle" style={{ width: '100%', height: '60px', marginBottom: '10px' }} />
    </>;


    return (
        <div>
            {((!actions && isFetching) || unpinning || completing) && <PartialSpinny />}
            <FilterBanner />
            <ActionPager onChange={handlePageChange} skip={page.skip} total={actions?.RowCount} take={page.take} />
            {notesToRender}
            {actions?.RowCount === 0 && <h6 className='m-3'>No actions found</h6>}
            <ActionPager onChange={handlePageChange} skip={page.skip} total={actions?.RowCount} take={page.take} />
        </div >

    );
};

interface PageInterface {
    skip: number;
    take: number;
}

export default Actions;